/**
 * condition-builder.tsx — catalog-driven multi-row condition editor.
 *
 * For probe_result types: shows AND/ANY toggle (single flat group).
 * For data_item types: rows only, no mode toggle (implicit AND).
 * When catalog has no condition_fields: shows "no fields" + raw-JSON fallback.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button, Textarea, SegmentedControl } from '@vnetwork/ui/primitives';
import type { WatchpointFieldCatalog } from '@/api/healthcheck';
import type { ConditionRow } from './condition-serializer';
import { ConditionRow as ConditionRowComp } from './ConditionRow';
import styles from './ConditionRow.module.css';

interface Props {
  catalog: WatchpointFieldCatalog;
  rows: ConditionRow[];
  mode: 'and' | 'or';
  onRowsChange: (rows: ConditionRow[]) => void;
  onModeChange: (mode: 'and' | 'or') => void;
  /** Passed through to show validation highlight on empty/incomplete rows */
  showErrors?: boolean;
  /** Raw-JSON fallback when existing conditions couldn't be parsed */
  rawFallback?: string | null;
  onRawFallbackChange?: (raw: string) => void;
}

export function ConditionBuilder({
  catalog,
  rows,
  mode,
  onRowsChange,
  onModeChange,
  showErrors,
  rawFallback,
  onRawFallbackChange,
}: Props) {
  const fields = catalog.condition_fields ?? [];
  const isProbeResult = catalog.eval_level === 'probe_result';
  const [useRaw, setUseRaw] = useState(rawFallback !== null && rawFallback !== undefined);

  const addRow = () => {
    const firstField = fields[0];
    onRowsChange([
      ...rows,
      {
        field: firstField?.name ?? '',
        operator: firstField?.operators[0] ?? '',
        value: '',
      },
    ]);
  };

  const updateRow = (i: number, patch: Partial<ConditionRow>) => {
    onRowsChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const removeRow = (i: number) => {
    onRowsChange(rows.filter((_, idx) => idx !== i));
  };

  // ---- Empty catalog fallback ------------------------------------------
  if (fields.length === 0) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
          No condition fields available for this monitor type. Use raw JSON to specify conditions.
        </p>
        <RawJsonEditor
          value={rawFallback ?? ''}
          onChange={onRawFallbackChange ?? (() => {})}
          showError={showErrors && !rawFallback?.trim()}
        />
      </div>
    );
  }

  // ---- Raw-JSON escape hatch when parse failed -------------------------
  if (useRaw && rawFallback !== undefined) {
    return (
      <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            Conditions could not be parsed into rows — editing as raw JSON.
          </span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setUseRaw(false);
              // Reset to empty rows when switching back
              onRowsChange([{ field: fields[0]?.name ?? '', operator: fields[0]?.operators[0] ?? '', value: '' }]);
              onRawFallbackChange?.('');
            }}
          >
            Switch to builder
          </Button>
        </div>
        <RawJsonEditor
          value={rawFallback ?? ''}
          onChange={onRawFallbackChange ?? (() => {})}
          showError={showErrors && !rawFallback?.trim()}
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {/* AND / ANY (OR) toggle — probe_result only, multi-row only */}
      {isProbeResult && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            Match
          </span>
          <ModeToggle value={mode} onChange={onModeChange} />
          <span style={{ fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
            of the following conditions:
          </span>
        </div>
      )}

      {!isProbeResult && (
        <p style={{ margin: 0, fontSize: 'var(--vn-font-size-sm)', color: 'var(--vn-fg-muted)' }}>
          All conditions must match (implicit AND).
        </p>
      )}

      {/* Condition rows */}
      <div>
        {/* Column headers */}
        <div className={styles.conditionHeader}>
          {['Field', 'Operator', 'Value', ''].map((h) => (
            <span
              key={h}
              style={{
                fontSize: 'var(--vn-font-size-xs)',
                color: 'var(--vn-fg-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
              }}
            >
              {h}
            </span>
          ))}
        </div>

        {rows.map((row, i) => (
          <ConditionRowComp
            key={i}
            row={row}
            fields={fields}
            onChange={(patch) => updateRow(i, patch)}
            onRemove={() => removeRow(i)}
            canRemove={rows.length > 1}
            showError={showErrors}
          />
        ))}
      </div>

      {showErrors && rows.length === 0 && (
        <p style={{ margin: 0, color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-sm)' }}>
          At least one condition is required.
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button size="sm" variant="secondary" startIcon={<Plus size={13} />} onClick={addRow}>
          Add condition
        </Button>
        {rawFallback === undefined ? null : (
          <Button size="sm" variant="ghost" onClick={() => setUseRaw(true)}>
            Edit as JSON
          </Button>
        )}
      </div>
    </div>
  );
}

// ---- Sub-components ------------------------------------------------------

const MODE_ITEMS = [
  { value: 'and', label: 'ALL' },
  { value: 'or', label: 'ANY' },
];

function ModeToggle({
  value,
  onChange,
}: {
  value: 'and' | 'or';
  onChange: (m: 'and' | 'or') => void;
}) {
  return (
    <SegmentedControl
      size="sm"
      items={MODE_ITEMS}
      value={value}
      onValueChange={(v) => onChange(v as 'and' | 'or')}
    />
  );
}

function RawJsonEditor({
  value,
  onChange,
  showError,
}: {
  value: string;
  onChange: (v: string) => void;
  showError?: boolean;
}) {
  return (
    <div>
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={'{\n  "status_code": { "$gte": 500 }\n}'}
        rows={6}
        style={{
          fontFamily: 'var(--vn-font-mono)',
          fontSize: 'var(--vn-font-size-sm)',
          width: '100%',
          border: showError ? '1px solid var(--vn-danger-text)' : undefined,
        }}
      />
      {showError && (
        <span style={{ color: 'var(--vn-danger-text)', fontSize: 'var(--vn-font-size-xs)' }}>
          Conditions JSON is required.
        </span>
      )}
    </div>
  );
}
